/**
 * CourseSquare — compact tile for a course (Kurs) running concurrently
 * with the class. Rendered below the main class lesson in the classic
 * Klassenbuch grid; several sit side by side.
 *
 * Open state is controlled by the parent so only one course popover is
 * open across the whole grid at a time.
 *
 * @module klassenbuch-kiosk/components/CourseSquare
 */
import type { KioskClassWeekCell } from "../types/kioskApiTypes";

interface CourseSquareProps {
  cell: KioskClassWeekCell;
  open: boolean;
  onToggle: () => void;
}

export function CourseSquare({ cell, open, onToggle }: CourseSquareProps) {
  const students = cell.students ?? [];
  const hasStudents = students.length > 0;
  const teacherLabels = (cell.teachers ?? [])
    .map((t) => t.kuerzel ?? t.name)
    .filter(Boolean);
  // Cancelled when a class absence (Ausflug …) covers this slot.
  const cancelled = !!cell.class_absence_label;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        title={cancelled ? `Entfällt — ${cell.class_absence_label}` : "Kinder anzeigen"}
        className={`flex flex-col items-start gap-0.5 rounded-md border px-1.5 py-1 min-w-[76px] max-w-[136px] text-left cursor-pointer transition-colors ${
          cancelled ? "opacity-60 " : ""
        }${
          open
            ? "border-violet-400 bg-violet-50"
            : "border-slate-200 bg-white hover:bg-slate-50"
        }`}
      >
        <span className="flex items-center gap-1 w-full">
          <span
            className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${
              cancelled ? "bg-slate-400" : "bg-violet-500"
            }`}
          />
          <span
            className={`text-[11px] font-semibold truncate ${
              cancelled ? "text-slate-500 line-through" : "text-slate-800"
            }`}
          >
            {cell.subject_abbreviation ?? cell.subject_name ?? "Kurs"}
          </span>
          {hasStudents ? (
            <span className="ml-auto text-[9px] font-bold text-violet-700 bg-violet-100 rounded px-1 flex-shrink-0">
              {students.length}
            </span>
          ) : null}
        </span>
        <span className="flex items-center justify-between gap-1 w-full">
          {teacherLabels.length > 0 ? (
            <span className="text-[9px] text-slate-400 truncate">
              {teacherLabels.join(", ")}
            </span>
          ) : (
            <span />
          )}
          {cell.room_number ? (
            <span
              className="text-[9px] text-slate-400 flex-shrink-0"
              title={cell.room_name ?? undefined}
            >
              {cell.room_number}
            </span>
          ) : null}
        </span>
      </button>
      {open ? (
        <div className="absolute z-20 mt-1 left-0 w-48 max-h-56 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg p-1.5">
          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-1 pb-1">
            {cell.subject_name ?? "Kurs"}
            {hasStudents ? ` · ${students.length} Kinder` : ""}
          </div>
          {hasStudents ? (
            students.map((s) => (
              <div
                key={s.id}
                className="text-xs text-slate-700 px-1 py-0.5 truncate"
              >
                {s.name ?? "—"}
                {s.is_guest ? (
                  <span className="text-[9px] text-amber-600"> (Gast)</span>
                ) : null}
              </div>
            ))
          ) : (
            <div className="text-xs text-slate-500 italic px-1 py-0.5">
              Keine angemeldeten Kinder
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
